import { Fragment, useEffect, useState, useRef, type ComponentType } from "react";
import { LgPhrase } from "../../../lg/LgPhrase";
import { ArrowRight, ExternalLink, Lock } from "lucide-react";
import { splitKeepTail } from "../MarkdownContent/rehypeKeepTail";
import { useViewerSignedIn } from "./viewer-auth";

interface OGPData {
  url: string;
  title: string | null;
  description: string | null;
  image: string | null;
  siteName: string | null;
  favicon: string | null;
}

interface LinkComponentProps {
  to: string;
  prefetch?: "none" | "intent" | "render" | "viewport";
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

interface OGPLinkCardProps {
  url: string;
  /** 本文に書いたリンク名。取りにいけない内部ページのカードの題名に使う */
  label?: string;
  LinkComponent?: ComponentType<LinkComponentProps>;
  /** 文節の切り方。渡されたら題と説明の文節の境に <wbr> を置く (置く側の keep-all の下で、行の端の途中や句読点の前で折れない) */
  phraseSegment?: (text: string) => string[];
}

// グローバルキャッシュ（同じURLへの重複リクエストを防ぐ）
const ogpCache = new Map<string, OGPData | null>();
const pendingRequests = new Map<string, Promise<OGPData | null>>();

// 内部リンクかどうかを判定（現行/旧ドメインまたは相対パス）
const isInternalLink = (urlString: string): boolean => {
  try {
    const parsed = new URL(urlString);
    return (
      parsed.hostname === "yumesaku.tech" ||
      parsed.hostname === "www.yumesaku.tech" ||
      parsed.hostname === "chotdekiru.com" ||
      parsed.hostname === "www.chotdekiru.com"
    );
  } catch {
    // 相対パスの場合
    return urlString.startsWith("/");
  }
};

// 内部リンクのパスを取得
const getInternalPath = (urlString: string): string => {
  try {
    const parsed = new URL(urlString);
    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    return urlString;
  }
};

// 公開コース配下のリンクだけは、認証ページを直接 scrape せずに
// コース情報専用 API から安全にタイトルとサムネイルを取得する。
const getInternalCourseSlug = (urlString: string): string | null => {
  let pathname = "";
  try {
    pathname = new URL(urlString).pathname;
  } catch {
    pathname = urlString.split(/[?#]/, 1)[0] ?? "";
  }

  const match = pathname.match(/^\/(?:learn\/)?courses\/([^/]+)/);
  if (!match) return null;

  try {
    const slug = decodeURIComponent(match[1]);
    return /^[a-z0-9-]+$/.test(slug) ? slug : null;
  } catch {
    return null;
  }
};

/*
  レッスンの住所 (/learn/courses/<コース>/<レッスン>) は、コースのカードにしない。
  前はコースの slug だけを見ていたので、同じコースの 4 つのレッスンのカードが
  4 枚とも同じコースの題と説明になり、どれがどのレッスンか見分けられなかった
*/
const getInternalLessonSlug = (urlString: string): string | null => {
  let pathname = "";
  try {
    pathname = new URL(urlString).pathname;
  } catch {
    pathname = urlString.split(/[?#]/, 1)[0] ?? "";
  }
  const match = pathname.match(/^\/learn\/courses\/[^/]+\/([^/]+)\/?$/);
  if (!match) return null;
  try {
    const slug = decodeURIComponent(match[1]);
    if (slug === "mistakes") return null;
    return /^[a-z0-9-]+$/.test(slug) ? slug : null;
  } catch {
    return null;
  }
};

/*
  研究所の記事も、外から取りにいかずに自分のデータから引く。

  `/internal/ogp` は相対パスを弾き、絶対 URL にしても Cloudflare 上では
  自分のオリジンを取れない。**内部リンクのカードは実データを一度も
  受け取れていなかった。** 記事なのに「ゆめさくコードを開く」と出ていたのは
  そのため。コースと同じように、公開記事だけを返す口から引く
*/
const LAB_NON_ARTICLE_PATHS = new Set([
  "/lab",
  "/lab/articles",
  "/lab/voices",
  "/lab/teachers",
  "/lab/founder",
  "/lab/guides",
  "/lab/faq",
  "/lab/uranai",
  "/lab/engineer-fortune",
  "/lab/job-diagnosis",
  "/tekishoku-shindan",
]);

const getInternalArticleSlug = (urlString: string): string | null => {
  let pathname = "";
  try {
    pathname = new URL(urlString).pathname;
  } catch {
    pathname = urlString.split(/[?#]/, 1)[0] ?? "";
  }
  pathname = pathname.replace(/\/$/, "");
  if (!pathname.startsWith("/lab/")) return null;
  if (LAB_NON_ARTICLE_PATHS.has(pathname)) return null;

  const segments = pathname.split("/").filter(Boolean);
  // /lab/<slug> と /lab/<節>/<slug> だけ。それより深いものは記事ではない
  if (segments.length < 2 || segments.length > 3) return null;

  try {
    const slug = decodeURIComponent(segments[segments.length - 1]);
    return /^[a-z0-9][a-z0-9-]*$/.test(slug) ? slug : null;
  } catch {
    return null;
  }
};

/*
  カードの下にかぶせる行き先の札。**外部リンクには出さない。**
  外はどこへ行くか分からないので「開く」としか書けず、書いても読む人の
  役に立たない。内部だけ、行った先で何ができるかを名詞で出す
*/
const internalCtaLabel = (urlString: string): string => {
  let pathname = "";
  try {
    pathname = new URL(urlString).pathname;
  } catch {
    pathname = urlString.split(/[?#]/, 1)[0] ?? "";
  }
  if (getInternalLessonSlug(urlString)) return "レッスンを見る";
  if (/^\/(?:learn\/)?courses\//.test(pathname)) return "コース詳細を見る";
  if (/^\/lab\/(?:voices|teachers)\b/.test(pathname)) return "インタビューを読む";
  if (/^\/lab\b/.test(pathname)) return "記事を読む";
  if (/^\/luagate\/videos\b/.test(pathname)) return "授業動画を見る";
  return "開く";
};

function CardHoverCta({ label }: { label: string }) {
  /*
    カードの下からせり上がる帯。/lab のカードの CardHoverCue と同じ形にしてある。
    同じ「押すとどこへ行くか」を、記事の中と一覧で別の見た目にしない

    平常時は透明。押せることは枠とカーソルで分かるので、行き先の名前は
    迷ったときだけ出ればいい。常時出すと本文の中で札が強すぎる。
    指で触る画面には出さない (md 未満)。hover が無いので、触れた瞬間に
    出て消えるだけになる
  */
  return (
    <span
      aria-hidden="true"
      className="ogp-link-card__cta pointer-events-none absolute inset-x-0 bottom-0 z-10 hidden min-h-9 translate-y-full items-center justify-between bg-primary-dark/90 px-3 max-sm:text-[0.9375rem] text-[14px] font-black text-white opacity-0 transition-[transform,opacity] duration-200 ease-out md:flex md:group-hover:translate-y-0 md:group-hover:opacity-100 md:group-focus-visible:translate-y-0 md:group-focus-visible:opacity-100"
    >
      {label}
      <ArrowRight className="h-3.5 w-3.5" />
    </span>
  );
}

// 内部リンクの OGP 取得失敗時に、URL から
// カテゴリ・ラベル・デフォルトサムネを推測して見栄えを良くする
// 旧ブランド「チョットデキル」の絵ではなく、いまのサイト共通の共有画像を出す
// (apps/client/app/lib/site-ogp.ts の SITE_OGP_IMAGE と同じ住所)
const DEFAULT_INTERNAL_OGP_IMAGE =
  "https://storage.chotdekiru.com/ogp/yumesaku_ogp_v2.png";
const inferInternalFallback = (
  urlString: string,
): { category: string; title: string; imageUrl: string } | null => {
  if (!isInternalLink(urlString)) return null;
  let pathname = "";
  try {
    pathname = new URL(urlString).pathname;
  } catch {
    pathname = urlString;
  }

  if (getInternalCourseSlug(urlString)) {
    return {
      category: "コース",
      title: "コースを開く",
      imageUrl: "/course-default/course.svg",
    };
  }

  const rules: Array<{ pattern: RegExp; category: string; title: string }> = [
    { pattern: /^\/luagate\/videos\b/, category: "ゆめさくエンジニア 動画", title: "授業動画を見る" },
    { pattern: /^\/luagate\/curriculum\b/, category: "ゆめさくエンジニア カリキュラム", title: "カリキュラムを開く" },
    { pattern: /^\/luagate\/homework\b/, category: "ゆめさくエンジニア 宿題", title: "宿題一覧を開く" },
    { pattern: /^\/luagate\/practical-projects\b/, category: "ゆめさくエンジニア 実践プロジェクト", title: "実践プロジェクトを開く" },
    { pattern: /^\/luagate\/articles\b/, category: "ゆめさくエンジニア 記事", title: "記事を読む" },
    { pattern: /^\/luagate\/resources\b/, category: "ゆめさくエンジニア 教材", title: "教材一覧を開く" },
    { pattern: /^\/luagate\/playground\b/, category: "ゆめさくエンジニア 実習", title: "実習の画面を開く" },
    { pattern: /^\/luagate\/learning-server\b/, category: "ゆめさくエンジニア 実習", title: "学習用サーバーの画面を開く" },
    { pattern: /^\/luagate\b/, category: "ゆめさくエンジニア", title: "学習ページを開く" },
    { pattern: /^\/lab\/voices\b/, category: "受講生の記録", title: "インタビューを読む" },
    { pattern: /^\/lab\/teachers\b/, category: "講師インタビュー", title: "インタビューを読む" },
    { pattern: /^\/lab\/founder\b/, category: "代表記事", title: "記事を読む" },
    { pattern: /^\/lab\b/, category: "ゆめさく研究所", title: "記事を読む" },
    { pattern: /^\/women\b/, category: "ゆめさくエンジニア", title: "ページを開く" },
    { pattern: /^\/learn\b/, category: "学習コンテンツ", title: "コンテンツを開く" },
    { pattern: /^\/courses\b/, category: "コース", title: "コースを開く" },
    { pattern: /^\/lessons\b/, category: "レッスン", title: "レッスンを開く" },
  ];

  for (const r of rules) {
    if (r.pattern.test(pathname)) {
      return { category: r.category, title: r.title, imageUrl: DEFAULT_INTERNAL_OGP_IMAGE };
    }
  }
  return { category: "ゆめさくコード", title: "ゆめさくコードを開く", imageUrl: DEFAULT_INTERNAL_OGP_IMAGE };
};

/** 文節の境に <wbr> を置く。切れ目は文節の中の字を 1 字も変えない */
function Phrased({ text, segment }: { text: string; segment?: (text: string) => string[] }) {
  if (!segment) return <>{text}</>;
  const segs = segment(text);
  return (
    <>
      {segs.map((seg, i) => (
        <Fragment key={i}>
          {i > 0 ? <wbr /> : null}
          {seg}
        </Fragment>
      ))}
    </>
  );
}

/** 説明の最後の 4 字を折らない塊にする (最後の行が「。」だけにならない)。rehypeKeepTail と同じ決め方 */
function KeepTail({ text, segment }: { text: string; segment?: (text: string) => string[] }) {
  // 本文 (phraseSegment を渡す側) のカードだけ。ほかの呼び出し元の折り方は変えない
  const parts = segment ? splitKeepTail(text) : null;
  if (!parts) return <Phrased text={text} segment={segment} />;
  return (
    <>
      <Phrased text={parts[0]} segment={segment} />
      <span className="md-tail" style={{ whiteSpace: "nowrap" }}>
        {parts[1]}
      </span>
    </>
  );
}

export function OGPLinkCard({ url, label, LinkComponent, phraseSegment }: OGPLinkCardProps) {
  const viewerSignedIn = useViewerSignedIn();
  const isInternal = isInternalLink(url);
  const internalCourseSlug = isInternal ? getInternalCourseSlug(url) : null;
  const internalArticleSlug = isInternal ? getInternalArticleSlug(url) : null;
  const internalLessonSlug = internalCourseSlug ? getInternalLessonSlug(url) : null;
  // コースの住所はコースのカード、レッスンの住所はレッスンごとのカード
  const cacheKey = internalLessonSlug
    ? `lesson:${internalCourseSlug}/${internalLessonSlug}`
    : internalCourseSlug
    ? `course:${internalCourseSlug}`
    : internalArticleSlug
      ? `article:${internalArticleSlug}`
      : url;
  const [ogpData, setOgpData] = useState<OGPData | null>(() => {
    // 初期状態でキャッシュをチェック
    return ogpCache.get(cacheKey) ?? null;
  });
  const [loading, setLoading] = useState(() => !ogpCache.has(cacheKey));
  const [error, setError] = useState(false);
  const mountedRef = useRef(true);

  const internalPath = isInternal ? getInternalPath(url) : "";

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // 認証必須の内部ページは OGP fetch しない。公開コースだけは専用 API から
    // DB のコース名・サムネイルを取得し、ログイン画面の OGP 混入を防ぐ。
    if (isInternal && !internalCourseSlug && !internalArticleSlug) {
      setOgpData(null);
      setLoading(false);
      setError(true);
      return;
    }
    // キャッシュにある場合はスキップ
    if (ogpCache.has(cacheKey)) {
      setOgpData(ogpCache.get(cacheKey) ?? null);
      setLoading(false);
      setError(ogpCache.get(cacheKey) === null);
      return;
    }

    const fetchOGP = async () => {
      // 既にリクエスト中の場合はそのPromiseを待つ
      const pendingRequest = pendingRequests.get(cacheKey);
      if (pendingRequest) {
        try {
          const data = await pendingRequest;
          if (mountedRef.current) {
            setOgpData(data);
            setLoading(false);
            setError(data === null);
          }
        } catch {
          if (mountedRef.current) {
            setError(true);
            setLoading(false);
          }
        }
        return;
      }

      setLoading(true);
      setError(false);

      const fetchPromise = (async (): Promise<OGPData | null> => {
        try {
          const endpoint = internalCourseSlug
            ? `/api/course-preview/${encodeURIComponent(internalCourseSlug)}${
                internalLessonSlug ? `?lesson=${encodeURIComponent(internalLessonSlug)}` : ""
              }`
            : internalArticleSlug
              ? `/api/article-preview/${encodeURIComponent(internalArticleSlug)}`
              : `/internal/ogp?url=${encodeURIComponent(url)}`;
          const response = await fetch(endpoint);

          if (!response.ok) {
            throw new Error("Failed to fetch OGP");
          }

          const data = (await response.json()) as OGPData;
          ogpCache.set(cacheKey, data);
          return data;
        } catch (err) {
          console.error("OGP fetch error:", err);
          ogpCache.set(cacheKey, null);
          return null;
        } finally {
          pendingRequests.delete(cacheKey);
        }
      })();

      pendingRequests.set(cacheKey, fetchPromise);

      try {
        const data = await fetchPromise;
        if (mountedRef.current) {
          setOgpData(data);
          setError(data === null);
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchOGP();
  }, [cacheKey, internalArticleSlug, internalCourseSlug, internalLessonSlug, isInternal, url]);

  // ドメイン名を抽出
  const getDomain = (urlString: string) => {
    try {
      const parsed = new URL(urlString);
      return parsed.hostname.replace(/^www\./, "");
    } catch {
      return urlString;
    }
  };

  // ローディング中
  if (loading) {
    return (
      <div className="ogp-link-card my-6 border border-gray-light/30 rounded-xl overflow-hidden animate-pulse">
        <div className="flex">
          <div className="flex-1 p-4 space-y-3">
            <div className="h-4 bg-gray-light/20 rounded w-3/4" />
            <div className="h-3 bg-gray-light/20 rounded w-full" />
            <div className="h-3 bg-gray-light/20 rounded w-1/2" />
          </div>
          <div className="w-[120px] h-[90px] bg-gray-light/20 shrink-0" />
        </div>
      </div>
    );
  }

  // エラー時もカード形式で表示（URLとドメインのみ）
  if (error || !ogpData) {
    // 内部ドメイン系の URL は path からカテゴリと擬似タイトルを推測してリッチに見せる
    const fallback = inferInternalFallback(url);
    /*
      カードの下の行。内部の住所 (/luagate など) をそのまま見せない。
      受講生の学習ページはログインが要るので、押す前にそれが分かる言葉にする
    */
    const needsLogin = viewerSignedIn !== true && isInternal && /^\/luagate(?:[/?#]|$)/.test(internalPath);
    const footText = needsLogin
      ? "ログインが必要です"
      : isInternal
        ? "ゆめさくコード"
        : getDomain(url);
    const errorContent = (
      <div className="flex">
        <div className="flex-1 p-4 min-w-0 flex flex-col justify-between">
          <div>
            {fallback?.category && (
              <span className="ogp-link-card__pill inline-block px-2 py-0.5 max-sm:text-[0.9375rem] text-[14px] font-bold rounded-full bg-[#e1f3ea] text-[#05b45b] mb-1">
                {fallback.category}
              </span>
            )}
            <h4 className="font-bold max-sm:text-[0.9375rem] text-sm text-main break-keep [overflow-wrap:anywhere]">
              <LgPhrase>{label?.trim() || fallback?.title || getDomain(url)}</LgPhrase>
            </h4>
          </div>
          <div className="flex items-center gap-2 mt-2">
            {needsLogin ? (
              <Lock className="w-3 h-3 shrink-0 text-sub" />
            ) : (
              <ExternalLink className="w-3 h-3 shrink-0 text-sub" />
            )}
            <span className="min-w-0 max-sm:text-[0.9375rem] text-sm text-sub [overflow-wrap:anywhere]">{footText}</span>
          </div>
        </div>
        {fallback?.imageUrl && (
          <div className="ogp-link-card__thumb w-[112px] shrink-0 sm:w-[220px] sm:h-[130px] bg-[#e1f3ea] flex items-center justify-center overflow-hidden">
            <img
              src={fallback.imageUrl}
              alt=""
              className="w-full h-full object-contain p-2"
              onError={(e) => {
                (e.target as HTMLImageElement).parentElement!.style.display =
                  "none";
              }}
            />
          </div>
        )}
        {isInternal && <CardHoverCta label={internalCtaLabel(url)} />}
      </div>
    );

    const errorCardClassName =
      "ogp-link-card group relative block my-6 border border-gray-light/30 rounded-xl overflow-hidden hover:shadow-md transition-all bg-white";

    // 内部リンクの場合は LinkComponent を使用
    if (isInternal && LinkComponent) {
      return (
        <LinkComponent
          to={internalPath}
          prefetch="intent"
          className={errorCardClassName}
        >
          {errorContent}
        </LinkComponent>
      );
    }

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={errorCardClassName}
      >
        {errorContent}
      </a>
    );
  }

  const cardContent = (
    <div className="flex">
      {/* コンテンツ */}
      <div className="flex-1 p-4 min-w-0 flex flex-col justify-between">
        {/* タイトル */}
        <h4
          className="ogp-link-card__title font-bold max-sm:text-[0.9375rem] text-sm text-main break-keep [overflow-wrap:anywhere]"
          style={{
            margin: "0px",
            color: "var(--ogp-card-title, #1D6FD3)",
            textDecorationLine: "none !important",
          }}
        >
          <Phrased text={ogpData.title || getDomain(url)} segment={phraseSegment} />
        </h4>

        {/* 説明 */}
        {ogpData.description && (
          <p
            className="max-sm:text-[0.9375rem] text-sm text-sub"
            style={{ margin: "0px", textDecorationLine: "none !important" }}
          >
            <KeepTail text={ogpData.description} segment={phraseSegment} />
          </p>
        )}
      </div>

      {/* サムネイル画像 */}
      {ogpData.image && (
        <div className="w-[112px] shrink-0 bg-second sm:w-[180px] sm:h-[105px]">
          <img
            src={ogpData.image}
            style={{
              borderTopLeftRadius: "0px",
              borderBottomLeftRadius: "0px",
            }}
            alt={ogpData.title || ""}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).parentElement!.style.display =
                "none";
            }}
          />
        </div>
      )}
      {isInternal && <CardHoverCta label={internalCtaLabel(url)} />}
    </div>
  );

  const cardClassName =
    "ogp-link-card group relative block my-6 border border-gray-light/30 rounded-xl overflow-hidden hover:shadow-md transition-all bg-white no-underline";
  const cardStyle = { textDecoration: "none", color: "inherit" } as const;

  // 内部リンクの場合は LinkComponent を使用
  if (isInternal && LinkComponent) {
    return (
      <LinkComponent
        to={internalPath}
        prefetch="intent"
        className={cardClassName}
        style={cardStyle}
      >
        {cardContent}
      </LinkComponent>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cardClassName}
      style={cardStyle}
    >
      {cardContent}
    </a>
  );
}
